<template>
	<base-modal
		ref="$modal"
		:height="490"
		:width="572"
		@on-close="deactivateEsc"
	>
		<div
			ref="$content"
			class="chat-resend-messages"
		>
			<app-loader v-if="isLoading" />
			<div class="chat-resend-messages__body">
				<div class="chat-resend-messages__body-search">
					<app-input
						ref="$input"
						v-model="filter"
						class="chat-resend-messages__body-search-input"
						:placeholder="$t('search_short')"
						show-search-button
					/>
				</div>
				<div
					v-if="!isLoading"
					class="chat-resend-messages__body-list"
					ref="$body"
				>
					<div
						v-if="!filteredReplayes.length"
						class="chat-resend-messages__body-list-empty"
					>
						{{ $t('no_matching_options') }}
					</div>
					<template v-else>
						<div
							v-for="replaye in filteredReplayes"
							class="chat-resend-messages__body-list-item"
							:key="replaye.Id"
							@click="onResendMessages(replaye)"
						>
							<img
								v-if="replaye.PhotoLink"
								class="chat-resend-messages__body-list-item-logo-img"
								:src="resolvePhotoUrl(replaye.PhotoLink)"
								alt="Avatar"
							/>
							<div
								v-else
								class="chat-resend-messages__body-list-item-logo-text"
							>
								{{ replaye.PhotoLiter }}
							</div>
							<div class="chat-resend-messages__body-list-item-text">
								{{ replaye.Name }}
							</div>
						</div>
					</template>
				</div>
			</div>
		</div>
	</base-modal>
</template>

<script setup lang="ts">
import {
	computed,
	ref,
	watch
} from "vue";
import BaseModal from "@/components/common/modal/BaseModal.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import { ChatsApi } from "@/ api/ChatsApi";
import { ChatMessage, Replayes } from "@/store/chats";
import { headerStore } from "@/store/header";
import { resolvePhotoUrl } from "@/utils/urlHelpers";
import { useModalEscape } from "@/composables/useModalEscape";

const emit = defineEmits<{
	(e: "on-resend"): void;
}>();

const store = headerStore()
const $modal = ref<typeof BaseModal | null>(null)
const $body = ref<HTMLDivElement | null>(null)
const $content = ref<HTMLElement | null>(null)
const isLoading = ref(false);
const replayes = ref<Replayes[] | null>(null)
const resendMessages = ref<ChatMessage[] | null>(null)
const filter = ref('')

const filteredReplayes = computed(() =>
	replayes.value
		? replayes.value.filter(replaye => replaye.Name.toLowerCase().includes(filter.value.toLowerCase()))
		: []
)

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose()
})

const onOpen = async (messages: ChatMessage[]) => {
	$modal.value?.onOpen();
	activateEsc();
	resendMessages.value = messages;

	try {
		isLoading.value = true;
		replayes.value = await ChatsApi.getChatListReplayes()
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false;
	}
}

const onResendMessages = async (replaye: Replayes) => {
	try {
		isLoading.value = true;
		let chatId = replaye.Id

		if (replaye.IsPotential) {
			const response = await ChatsApi.createChatWithYouOrOneFriend(replaye.Id)
			chatId = response.Id
			await store.updateDialogInList(getCurrentDate())
		}

		store.setResendMessages({
			chatId,
			messages: resendMessages.value
		})
		$modal.value?.onClose()
		const dialog = store.getDialogs.find(d => d.Id === chatId)
		if (dialog) {
			store.addOpenedChatsHistory(dialog)
		}
		store.setMode('chat')
		await store.setOpenedChat(chatId)
		emit('on-resend')
	} catch (e) {
		console.error(e)
	} finally {
		isLoading.value = false;
	}
}

const getCurrentDate = () => {
	const date = new Date();

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');
	const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
}

watch(filter, () => {
	$body.value?.scrollTo({
		top: 0
	});
})

defineExpose({ onOpen })
</script>

<style scoped lang="scss">
.chat-resend-messages {
	display: flex;
	flex-direction: column;
	position: relative;
	height: 100%;
	padding: 12px 12px 0;

	&__body {
		display: flex;
		flex-direction: column;
		max-height: 100%;

		&-search {
			display: flex;
			gap: 8px;
			width: 100%;
			margin-bottom: 8px;

			&-input {
				width: 100%;
			}
		}

		&-list {
			display: flex;
			flex: 1;
			flex-direction: column;
			gap: 8px;
			overflow: auto;

			&-empty {
				display: flex;
				align-items: center;
				justify-content: center;
				padding: 16px;
				color: #4D4D4D;
			}

			&-item {
				display: flex;
				align-items: center;
				gap: 8px;
				font-size: 14px;
				line-height: 18px;
				color: #333;
				cursor: pointer;
				transition: color 0.1s;

				&:hover {
					.chat-resend-messages__body-list-item-text {
						text-decoration: underline;
						color: #396742;
					}
				}

				&-text {
					font-size: 14px;
					line-height: 18px;
				}

				&-logo {
					display: flex;
					align-items: center;
					height: 30px;
					width: 30px;

					&-img,
					&-text {
						height: 30px;
						min-width: 30px;
						max-width: 30px;
						border-radius: 50%;
					}

					&-text {
						display: flex;
						align-items: center;
						justify-content: center;
						font-size: 18px;
						line-height: 18px;
						text-transform: uppercase;
						color: #fff;
						background-color: #E6C15A;
					}
				}
			}
		}
	}
}
</style>
